import { useState, useCallback } from 'react';
import {
  Item,
  ItemResponse,
  SearchItemsRequest,
  ACLAuthorizationRequest,
  SearchItemsPostContentTypeEnum,
  CreateItemContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getItemsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseItemsReturn {
  items: ItemResponse[];
  loading: boolean;
  searchError: string | null;
  selectedItem: ItemResponse | null;
  setSelectedItem: (item: ItemResponse | null) => void;
  fetchItems: (page?: number, pageSize?: number, searchBody?: SearchItemsRequest) => Promise<boolean>;
  createItem: (itemData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useItems = (): UseItemsReturn => {
  const [items, setItems] = useState<ItemResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ItemResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchItems = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchItemsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getItemsApi();
      const response = await api.searchItemsPost({
        contentType: SearchItemsPostContentTypeEnum.ApplicationJson,
        searchItemsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch items');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createItem = useCallback(async (itemData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getItemsApi();

      const createdItem = await api.createItem({
        contentType: CreateItemContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        item: itemData as Item,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdItemId = createdItem?.id;

      showSuccess(
        'Item created successfully',
        createdItemId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('item', createdItemId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create item', 'Failed to create item');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteItem = useCallback(async (itemId: string) => {
    try {
      const api = getItemsApi();
      await api.deleteItemById({ itemId });
      showSuccess('Item deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete item', 'Failed to delete item');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    items: items,
    loading,
    searchError,
    selectedItem,
    setSelectedItem,
    fetchItems,
    createItem,
    deleteItem,
    setTotal,
    total,
  };
};
